/**
 * Calendar Data Service
 * 
 * Handles fetching and merging calendar data from multiple sources:
 * 1. getEvents (Cronofy API) - Returns managed + external events
 * 2. queryByOwner (MeetingDB) - Returns meetings where user is booker/attendee
 * 
 * Implements merge logic to deduplicate when attendees accept email invites.
 */

import { apiGet } from 'app/shared-components/util/restAPI';
import { getCronofyEvents } from 'app/shared-components/util/cronofyApi';

/**
 * Fetch and merge all calendar data for a user
 * @param {string} userId - Current user ID
 * @param {string} from - Start date (YYYY-MM-DD)
 * @param {string} to - End date (YYYY-MM-DD)
 * @returns {Promise<Array>} Merged calendar events
 */
export async function fetchMergedCalendarData(userId, from, to) {
  // 1. Fetch calendar events (includes managed meetings where user is organizer)
  const events = await fetchCalendarEvents(from, to);
  
  // 2. Fetch meetings where user is booker/attendee
  const bookedMeetings = await fetchBookedMeetings(userId, from, to);
  
  // 3. Merge and deduplicate
  return mergeCalendarData(events, bookedMeetings, userId);
}

/**
 * Fetch calendar events from Cronofy API
 */
async function fetchCalendarEvents(from, to) {
  try {
    const response = await getCronofyEvents(from, to);
    
    // Transform Cronofy events to our format
    return (response.events || []).map(event => {
      const tags = extractTagsFromCronofyEvent(event);
      
      return {
        eventId: event.event_id,
        cronofyId: event.event_uid,
        title: event.summary,
        summary: event.summary,
        description: event.description,
        start: event.start,
        end: event.end,
        location: typeof event.location === 'string' ? event.location : (event.location?.description || ''),
        calendarId: event.calendar_id,
        attendees: event.attendees || [],
        organizer: event.organizer,
        organizerEmail: event.organizer?.email,
        conferencing: event.conferencing,
        tags,
        originalEvent: event
      };
    });
  } catch (error) {
    console.error('Failed to fetch calendar events:', error);
    return [];
  }
}

/**
 * Extract tags from Cronofy event
 */
function extractTagsFromCronofyEvent(event) {
  if (!event.tags || !event.tags.private) return [];
  return event.tags.private.map(tag => tag.value).filter(Boolean);
}

/**
 * Fetch meetings where user is booker/attendee
 */
async function fetchBookedMeetings(userId, from, to) {
  try {
    const ownerKey = `User:${userId}`;
    const response = await apiGet('CoreAPI3', `/x3/meeting/byowner/${ownerKey}`, {});
    
    // Filter by date range
    const fromTime = new Date(from).getTime();
    const toTime = new Date(to).getTime();
    
    return (response || []).filter(meeting => {
      const startTime = new Date(meeting.eventInfo.start).getTime();
      return startTime >= fromTime && startTime <= toTime;
    });
  } catch (error) {
    console.error('Failed to fetch booked meetings:', error);
    return [];
  }
}

/**
 * Merge calendar events and booked meetings, deduplicating where necessary
 */
function mergeCalendarData(events, bookedMeetings, currentUserId) {
  const merged = [];
  const processedEventIds = new Set();
  
  // Process calendar events
  for (const event of events) {
    if (isMeetingEvent(event)) {
      // This is a managed meeting where user is organizer
      // meetingId will be fetched on-demand using eventId
      merged.push({
        type: 'meeting',
        role: 'organizer',
        eventId: event.eventId,
        ...event,
        icon: '📥',
        label: getOrganizerLabel(event),
        resource: {
          isManaged: true,
          isMeeting: true,
          role: 'organizer'
        }
      });
    } else if (isSessionEvent(event)) {
      // This is a session
      merged.push({
        type: 'event',
        eventId: event.eventId,
        ...event,
        resource: {
          isManaged: true,
          isSession: true
        }
      });
    } else {
      // Regular event (personal or external)
      merged.push({
        type: 'event',
        eventId: event.eventId,
        ...event,
        resource: {
          isManaged: !!event.eventId,
          isSession: false
        }
      });
    }
    if (event.eventId) processedEventIds.add(event.eventId);
  }
  
  // Process booked meetings (may match external events from accepted invites)
  for (const meeting of bookedMeetings) {
    const matchingEvent = findMatchingExternalEvent(events, meeting);
    
    merged.push({
      type: 'meeting',
      role: 'attendee',
      meetingId: meeting.id,
      eventId: matchingEvent?.eventId,
      start: meeting.eventInfo.start,
      end: meeting.eventInfo.end,
      title: meeting.eventInfo.summary,
      summary: meeting.eventInfo.summary,
      description: meeting.eventInfo.description,
      location: meeting.eventInfo.location,
      conferencing: meeting.eventInfo.conferencing,
      organizerUserId: meeting.organizerUserId,
      attendeeUserIds: meeting.attendeeUserIds,
      meetingTypeId: meeting.meetingTypeId,
      icon: '📤',
      label: getAttendeeLabel(meeting),
      syncedToCalendar: !!matchingEvent,
      resource: {
        isManaged: true,
        isMeeting: true,
        meetingId: meeting.id,
        role: 'attendee',
        syncedToCalendar: !!matchingEvent
      }
    });
    
    // Remove matching external event to avoid duplicate display
    if (matchingEvent) {
      const index = merged.findIndex(e => e.eventId === matchingEvent.eventId);
      if (index !== -1) merged.splice(index, 1);
    }
  }
  
  return merged;
}

/**
 * Check if event is a meeting (has MeetingType tag)
 */
function isMeetingEvent(event) {
  return event.tags?.some(tag => tag.startsWith('MeetingType:'));
}

/**
 * Check if event is a session (has ClientService tag)
 */
function isSessionEvent(event) {
  return event.tags?.some(tag => tag.startsWith('ClientService:'));
}

/**
 * Find matching external event for a booked meeting
 */
function findMatchingExternalEvent(events, meeting) {
  return events.find(event => {
    // Must be external (not a managed meeting)
    if (isMeetingEvent(event)) return false;
    
    // Match time (exact)
    if (event.start !== meeting.eventInfo.start) return false;
    if (event.end !== meeting.eventInfo.end) return false;
    
    // Match title (exact)
    if (event.summary !== meeting.eventInfo.summary) return false;
    
    // Match organizer email (if available)
    // Note: We'd need to fetch organizer user data to compare emails
    // For now, time + title match is sufficient
    
    return true;
  });
}

/**
 * Get label for organizer role
 */
function getOrganizerLabel(event) {
  const attendeeCount = event.attendees?.length || 0;
  if (attendeeCount === 0) return 'Meeting';
  if (attendeeCount === 1) return `${event.attendees[0].name || 'Someone'} booked with you`;
  return `${attendeeCount} people booked with you`;
}

/**
 * Get label for attendee role
 */
function getAttendeeLabel(meeting) {
  // We'd need to fetch organizer user data to get their name
  // For now, use a placeholder
  return `You booked a meeting`;
}

/**
 * Convert merged data to calendar event format
 */
export function toCalendarEvents(mergedData) {
  return mergedData.map(item => ({
    id: item.eventId || item.meetingId,
    title: item.title || item.summary,
    start: new Date(item.start),
    end: new Date(item.end),
    resource: {
      ...item.resource,
      ...item,
      isManaged: item.type === 'meeting' || item.resource?.isManaged,
      isMeeting: item.type === 'meeting',
      isSession: item.resource?.isSession
    }
  }));
}
